import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  overviewToneColor,
  type OverviewIssue,
} from '../../lib/dashboard/overview/buildOverviewStatus';
import { displayManagePath } from '../displays/manage/displayManageRoutes';
import { HomeRailPanel } from './HomeRailPanel';

const VISIBLE_ISSUES = 4;

type HomeAttentionRailProps = {
  issues: OverviewIssue[];
  hasScreens: boolean;
  canManage: boolean;
};

function StatusLine({ tone, text }: { tone: 'ok' | 'muted'; text: string }) {
  return (
    <p className="m-0 flex items-center gap-2.5 text-sm" style={{ color: 'var(--gs-muted)' }}>
      <span
        className="h-[9px] w-[9px] shrink-0 rounded-full"
        style={{ background: overviewToneColor(tone) }}
        aria-hidden
      />
      {text}
    </p>
  );
}

/** Screens that are offline, not reporting, not set up, or showing nothing. */
export function HomeAttentionRail({ issues, hasScreens, canManage }: HomeAttentionRailProps) {
  const { t } = useTranslation('home');
  const hidden = issues.length - VISIBLE_ISSUES;

  return (
    <HomeRailPanel
      title={t('attention.title')}
      link={issues.length ? { label: t('attention.allScreens'), to: '/screens' } : undefined}
    >
      {!hasScreens ? (
        <>
          <StatusLine tone="muted" text={t('attention.noScreens')} />
          {canManage ? (
            <Link
              to="/screens#setup"
              className="self-start text-[13px] font-bold"
              style={{ color: 'var(--gs-accent)' }}
            >
              {t('attention.pairScreen')}
            </Link>
          ) : null}
        </>
      ) : issues.length === 0 ? (
        <StatusLine tone="ok" text={t('attention.allGood')} />
      ) : (
        <>
          {issues.slice(0, VISIBLE_ISSUES).map((issue) => (
            <Link
              key={issue.screenId}
              to={displayManagePath(issue.screenId)}
              className="flex items-start gap-3 transition hover:opacity-90"
              style={{ color: 'var(--gs-ink)' }}
            >
              <span
                className="mt-[5px] h-[9px] w-[9px] shrink-0 rounded-full"
                style={{ background: overviewToneColor(issue.tone) }}
                aria-hidden
              />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm font-bold">{issue.name}</span>
                <span className="text-[12.5px]" style={{ color: 'var(--gs-muted)' }}>
                  {issue.reason}
                </span>
              </span>
              <span className="shrink-0 text-xs font-bold" style={{ color: 'var(--gs-accent)' }}>
                {issue.action}
              </span>
            </Link>
          ))}
          {hidden > 0 ? (
            <Link to="/screens" className="text-[13px] font-bold" style={{ color: 'var(--gs-muted)' }}>
              {t('attention.more', { hidden })}
            </Link>
          ) : null}
        </>
      )}
    </HomeRailPanel>
  );
}
